import {ChildCaseModel} from '../model/child-case.model';
import {VisitModel} from '../model/visit.model';

/**
 * Diagnóstico dos Indicadores de Crescimento.
 *
 * Ordem de prioridade (primeira regra satisfeita define o resultado):
 *   0. Porta de entrada — precisa de ao menos um de {MUAC, IMC, Peso×Talla}.
 *   1. Altura ∈{SD3-,SD2-}  E  déficit agudo ∈{SD3-,SD2-}   → "Desnutrición crónica agudizada"
 *   2. MUAC:  SD3- → Aguda Severa · SD2- → Aguda Moderada · SD1- → Riesgo de Desnutrición Aguda
 *   3. Déficit agudo ∈{SD3-,SD2-} com Altura ∈{SD1-,SD0,SD1} → Crónica Severa/Moderada
 *   4. Altura ∈{SD3-,SD2-} isolada                          → Crónica Severa/Moderada
 *   5. Peso×Talla SD1/SD2/SD3 → Riesgo de sobrepeso / Sobrepeso / Obesidad
 *   6. IMC        SD1/SD2/SD3 → Riesgo de sobrepeso / Sobrepeso / Obesidad
 *   7. Todos os indicadores DISPONÍVEIS na faixa verde     → "Normal"
 *   8. Fallback                                            → "Resultado no identificado"
 *
 * "Déficit agudo" = Peso×Talla quando disponível; se a estatura passa de 120 cm
 * (fora da tabela da OMS), usa o IMC-para-idade como proxy de emagrecimento.
 *
 * calculateDiagnosisWarning() devolve um aviso (não muda o diagnóstico) quando
 * um indicador disponível aponta um quadro mais grave ou diferente do que o
 * diagnóstico capturou.
 */
export class DiagnosisHelper {

    // Textos padronizados em "sentence case", iguais a MalnutritionPhaseHelper /
    // MuacLevel / ImcLevel / HeightToWeight.
    private static readonly UNKNOWN_RESULT = 'Resultado no identificado';
    private static readonly CHRONIC_ACUTE_MALNUTRITION = 'Desnutrición crónica agudizada';
    private static readonly SEVERE_CHRONIC_MALNUTRITION = 'Desnutrición crónica severa';
    private static readonly MODERATE_CHRONIC_MALNUTRITION = 'Desnutrición crónica moderada';
    private static readonly SEVERE_ACUTE_MALNUTRITION = 'Desnutrición aguda severa';
    private static readonly MODERATE_ACUTE_MALNUTRITION = 'Desnutrición aguda moderada';
    private static readonly RISK_OF_ACUTE_MALNUTRITION = 'Riesgo de desnutrición aguda';
    private static readonly OVERWEIGHT_RISK = 'Riesgo de sobrepeso';
    private static readonly OVERWEIGHT = 'Sobrepeso';
    private static readonly OBESITY = 'Obesidad';
    private static readonly NORMAL = 'Normal';
    private static readonly NORMAL_MUAC_ONLY = 'Normal (evaluación solo por MUAC)';

    public calculateChildDiagnosis(child: ChildCaseModel): string {
        return this.assess(child);
    }

    public calculateVisitDiagnosis(visit: VisitModel): string {
        return this.assess(visit);
    }

    // ------------------------------------------------------------------ //

    /**
     * Converte a classe de indicador (ex.: 'sd2neg-wh', 'sd0-imc', 'sd3neg') no
     * nível de desvio-padrão (-3..3). Retorna null quando o indicador não foi
     * medido / não tem tabela ('', 'none', 'Unknown', ...).
     * 'sd3-muac-healed' (criança recuperada) conta como verde (SD3).
     */
    private toSd(value: string | null | undefined): number | null {
        if (!value) {
            return null;
        }
        const v = value.toLowerCase().trim();
        if (v.endsWith('-healed')) {
            return 3;
        }
        switch (v.replace(/-(muac|imc|wh)$/, '')) {
            case 'sd3neg': return -3;
            case 'sd2neg': return -2;
            case 'sd1neg': return -1;
            case 'sd0':    return 0;
            case 'sd1':    return 1;
            case 'sd2':    return 2;
            case 'sd3':    return 3;
            default:       return null; // '', 'none', 'unknown', etc.
        }
    }

    private indicators(source: ChildCaseModel | VisitModel) {
        return {
            height: this.toSd(source.heightRangeIndicator),        // Talla x Edad
            weightAge: this.toSd(source.weightRangeIndicator),     // Peso x Edad
            imc: this.toSd(source.imcRangeIndicator),              // IMC x Edad
            wh: this.toSd(source.heightToWeightIndicator),         // Peso x Talla
            muac: this.toSd(source.rangeIndicator),                // MUAC x Edad
        };
    }

    private assess(source: ChildCaseModel | VisitModel): string {
        const {height, weightAge, imc, wh, muac} = this.indicators(source);

        // 0. Porta de entrada — sem nenhum indicador de nutrição não dá para avaliar.
        if (muac == null && imc == null && wh == null) {
            return DiagnosisHelper.UNKNOWN_RESULT;
        }

        // Déficit agudo: Peso×Talla; se ausente (> 120 cm), IMC como proxy.
        const acuteDeficit = wh != null ? wh : imc;

        // 1. Baixa estatura + déficit agudo → crónica agudizada
        if (height != null && height <= -2 && acuteDeficit != null && acuteDeficit <= -2) {
            return DiagnosisHelper.CHRONIC_ACUTE_MALNUTRITION;
        }

        // 2. MUAC em déficit
        if (muac != null && muac <= -1) {
            if (muac <= -3) {
                return DiagnosisHelper.SEVERE_ACUTE_MALNUTRITION;
            }
            if (muac === -2) {
                return DiagnosisHelper.MODERATE_ACUTE_MALNUTRITION;
            }
            return DiagnosisHelper.RISK_OF_ACUTE_MALNUTRITION; // -1
        }

        // 3. Déficit agudo com estatura normal → crónica
        if (acuteDeficit != null && acuteDeficit <= -2 &&
            height != null && height >= -1 && height <= 1) {
            return acuteDeficit <= -3
                ? DiagnosisHelper.SEVERE_CHRONIC_MALNUTRITION
                : DiagnosisHelper.MODERATE_CHRONIC_MALNUTRITION;
        }

        // 4. Baixa estatura isolada → crónica
        if (height != null && height <= -2) {
            return height <= -3
                ? DiagnosisHelper.SEVERE_CHRONIC_MALNUTRITION
                : DiagnosisHelper.MODERATE_CHRONIC_MALNUTRITION;
        }

        // 5. Sobrepeso por Peso × Talla
        if (wh != null && wh >= 1) {
            return wh >= 3 ? DiagnosisHelper.OBESITY
                : wh === 2 ? DiagnosisHelper.OVERWEIGHT
                    : DiagnosisHelper.OVERWEIGHT_RISK;
        }

        // 6. Sobrepeso por IMC
        if (imc != null && imc >= 1) {
            return imc >= 3 ? DiagnosisHelper.OBESITY
                : imc === 2 ? DiagnosisHelper.OVERWEIGHT
                    : DiagnosisHelper.OVERWEIGHT_RISK;
        }

        // 7. Normal — todos os indicadores DISPONÍVEIS na faixa verde
        if (this.allAvailableGreen(height, weightAge, imc, wh, muac)) {
            const onlyMuac = muac != null && imc == null && wh == null &&
                height == null && weightAge == null;
            return onlyMuac ? DiagnosisHelper.NORMAL_MUAC_ONLY : DiagnosisHelper.NORMAL;
        }

        // 8. Fallback
        return DiagnosisHelper.UNKNOWN_RESULT;
    }

    /** Faixas verdes (só bloqueia "Normal" um indicador presente fora da faixa). */
    private allAvailableGreen(height: number | null, weightAge: number | null,
                              imc: number | null, wh: number | null, muac: number | null): boolean {
        if (muac == null && imc == null) {
            return false; // precisa de ao menos MUAC ou IMC medido
        }
        const inRange = (v: number | null, lo: number, hi: number) => v == null || (v >= lo && v <= hi);
        return inRange(muac, 0, 3) &&      // SD0..SD3
            inRange(imc, -1, 0) &&         // SD1-, SD0
            inRange(height, -1, 1) &&      // SD1-, SD0, SD1
            inRange(weightAge, -1, 1) &&   // SD1-, SD0, SD1
            inRange(wh, -1, 0);            // SD1-, SD0
    }

    // ------------------------------------------------------------------ //

    /**
     * Aviso complementar (não altera o diagnóstico): mostrado quando um indicador
     * disponível aponta um quadro mais grave ou diferente do que a regra que
     * disparou capturou.
     */
    public calculateDiagnosisWarning(source: ChildCaseModel | VisitModel): string {
        if (!source) {
            return '';
        }
        const {height, weightAge, imc, wh} = this.indicators(source);
        const dx = this.assess(source);
        const acuteDeficit = wh != null ? wh : imc;

        const isMildAcuteDx = dx === DiagnosisHelper.MODERATE_ACUTE_MALNUTRITION ||
            dx === DiagnosisHelper.RISK_OF_ACUTE_MALNUTRITION;
        const isChronicDx = dx === DiagnosisHelper.SEVERE_CHRONIC_MALNUTRITION ||
            dx === DiagnosisHelper.MODERATE_CHRONIC_MALNUTRITION ||
            dx === DiagnosisHelper.CHRONIC_ACUTE_MALNUTRITION;
        const isOverweightDx = dx === DiagnosisHelper.OVERWEIGHT_RISK ||
            dx === DiagnosisHelper.OVERWEIGHT || dx === DiagnosisHelper.OBESITY;
        const isNormalDx = dx === DiagnosisHelper.NORMAL || dx === DiagnosisHelper.NORMAL_MUAC_ONLY;

        const notes: string[] = [];

        // Severidade aguda mascarada (ex.: MUAC SD2- + Peso×Talla SD3-).
        if (isMildAcuteDx && acuteDeficit != null && acuteDeficit <= -3) {
            notes.push((wh != null ? 'Peso × Talla' : 'IMC') +
                ' en SD3- (severo): evaluar criterios de desnutrición aguda severa');
        }

        // Baixa estatura não capturada pelo diagnóstico.
        if (!isChronicDx && height != null && height <= -2) {
            notes.push('Talla en ' + (height <= -3 ? 'SD3-' : 'SD2-') + ': indica desnutrición crónica');
        }

        // Sobrepeso não capturado (ex.: baixa estatura + sobrepeso).
        if (!isOverweightDx) {
            if (wh != null && wh >= 2) {
                notes.push('Peso × Talla en ' + (wh >= 3 ? 'SD3: indica obesidad' : 'SD2: indica sobrepeso'));
            } else if (wh == null && imc != null && imc >= 2) {
                notes.push('IMC en ' + (imc >= 3 ? 'SD3: indica obesidad' : 'SD2: indica sobrepeso'));
            }
        }

        // Baixo peso para a idade num quadro "Normal".
        if (isNormalDx && weightAge != null && weightAge <= -2) {
            notes.push('Peso para la edad en ' + (weightAge <= -3 ? 'SD3-' : 'SD2-') + ' (bajo peso)');
        }

        return notes.length ? '⚠ ' + notes.join(' · ') : '';
    }
}
